import { Helmet } from "react-helmet-async";
import { SITE_URL, SITE_NAME } from "@/config/site";

/**
 * SEOHead - sets meta title/description, canonical link, Open Graph + Twitter
 * tags, optional keywords, and optional ImageObject + page JSON-LD.
 */
export default function SEOHead({
  title,
  description,
  path = "/",
  image = "https://images.unsplash.com/photo-1610502778270-c5c6f4c7d575?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
  imageAlt,
  keywords,
  jsonLd,
  imageObjects = [],
  noindex = false,
}) {
  const canonical = `${SITE_URL}${path}`;
  const fullTitle =
    title.includes(SITE_NAME) || `${title} | ${SITE_NAME}`.length > 60
      ? title
      : `${title} | ${SITE_NAME}`;

  // Optional ImageObject JSON-LD entries
  const imageSchemas = imageObjects.map((img) => ({
    "@context": "https://schema.org",
    "@type": "ImageObject",
    contentUrl: img.url,
    url: img.url,
    name: img.name || img.alt,
    caption: img.alt,
    description: img.alt,
    creditText: SITE_NAME,
    creator: { "@type": "Organization", name: SITE_NAME },
    copyrightNotice: `© ${new Date().getFullYear()} ${SITE_NAME}`,
    license: SITE_URL,
    acquireLicensePage: `${SITE_URL}/#contact`,
    representativeOfPage: img.representative === true,
    ...(img.width ? { width: img.width } : {}),
    ...(img.height ? { height: img.height } : {}),
  }));

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      <meta property="og:locale" content="en_NZ" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}

      {imageSchemas.map((s, i) => (
        <script key={`img-schema-${i}`} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
